import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { apiFetch, apiPost, friendlyError, type ApiError } from "../../api/client.js";
import { formatBytes } from "./vmUtils.js";

interface IsoEntry {
  id: string;
  display_name: string;
  size_bytes: number;
  present: boolean;
}

interface IsoPickerModalProps {
  vmid: number;
  name: string;
  opened: boolean;
  onClose: () => void;
  /** Phase 31: the VM detail's customer-safe mounted-ISO handle
   * (opaque id + display name) — no storage/volume identity. */
  mountedIso: { id: string; name: string } | null;
  onChanged: () => void;
}

/*
 * ISO picker. Lists the mountable ISOs (GET /api/isos — opaque `id` +
 * display_name, Phase 31) and mounts/unmounts via the smart-slot
 * backend, addressing ISOs by `iso_id`. "Currently mounted" comes from
 * the VM detail's `mounted_iso` field, passed in as `mountedIso` by
 * VmDetail. On any mount/unmount success we refetch the VM detail
 * (onChanged) so the card + badge stay truthful, and refresh the ISO
 * list so the "mounted" tag moves with the state.
 *
 * Error mapping (matches the backend's response shapes):
 *   409 no_free_slot -> "No free device slot on this VM. Contact support."
 *   409 no_cdrom     -> "No ISO is currently mounted."
 *   403 not_in_allowlist -> "This ISO is not allowed." (defense in depth;
 *                           shouldn't happen via UI since we only list
 *                           allowed ISOs)
 *   502 pve_unavailable -> generic "Proxmox is unavailable."
 */
export function IsoPickerModal({
  vmid,
  name,
  opened,
  onClose,
  mountedIso,
  onChanged,
}: IsoPickerModalProps) {
  const [isos, setIsos] = useState<IsoEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  async function loadIsos() {
    setLoading(true);
    try {
      const data = await apiFetch<IsoEntry[]>("/api/isos");
      setIsos(data);
    } catch (err) {
      notifications.show({ color: "red", message: friendlyError(err, "Could not load ISO list.") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (opened) void loadIsos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  function notifyMountError(err: unknown) {
    const e = err as ApiError;
    if (e.status === 409 && e.error === "no_free_slot") {
      notifications.show({ color: "red", message: "No free device slot on this VM. Contact support." });
    } else if (e.status === 403 && e.error === "not_in_allowlist") {
      notifications.show({ color: "red", message: "This ISO is not allowed." });
    } else if (e.status === 502 && e.error === "pve_unavailable") {
      notifications.show({ color: "red", message: "Proxmox is unavailable. Please try again." });
    } else {
      notifications.show({ color: "red", message: friendlyError(e, "Mount failed.") });
    }
  }

  async function mount(iso: IsoEntry) {
    setBusy(true);
    try {
      const res = await apiPost<{ boot_order_updated?: boolean }>(`/api/vms/${vmid}/mount-iso`, {
        iso_id: iso.id,
      });
      if (res.boot_order_updated === false) {
        // Best-effort signal from the backend: ISO is mounted, but the
        // boot-order PUT failed (PVE hiccup). The next mount retries
        // idempotently — tell the customer so they know to retry.
        notifications.show({
          color: "yellow",
          message: "ISO mounted, but boot-order update failed — remount to retry.",
        });
      } else {
        notifications.show({ color: "green", message: "ISO mounted." });
      }
      onChanged();
      void loadIsos();
    } catch (err) {
      notifyMountError(err);
    } finally {
      setBusy(false);
    }
  }

  function confirmUnmount() {
    modals.openConfirmModal({
      title: "Unmount ISO",
      centered: true,
      children: (
        <Text size="sm">
          Unmount the ISO from <b>{name}</b>? The CD-ROM device stays attached but empty.
        </Text>
      ),
      labels: { confirm: "Unmount", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => void doUnmount(),
    });
  }

  async function doUnmount() {
    setBusy(true);
    try {
      await apiPost(`/api/vms/${vmid}/unmount-iso`);
      notifications.show({ color: "green", message: "ISO unmounted." });
      onChanged();
      void loadIsos();
    } catch (err) {
      const e = err as ApiError;
      if (e.status === 409 && e.error === "no_cdrom") {
        notifications.show({ color: "red", message: "No ISO is currently mounted." });
      } else if (e.status === 502 && e.error === "pve_unavailable") {
        notifications.show({ color: "red", message: "Proxmox is unavailable. Please try again." });
      } else {
        notifications.show({ color: "red", message: friendlyError(e, "Unmount failed.") });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={mountedIso ? "Manage ISO" : "Mount ISO"}
      size="lg"
    >
      <Stack gap="md">
        {mountedIso && (
          <Card withBorder radius="md" p="sm">
            <Group justify="space-between" align="center">
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Currently mounted
                </Text>
                <Text>{mountedIso.name}</Text>
              </Stack>
              <Button color="red" variant="light" loading={busy} onClick={confirmUnmount}>
                Unmount
              </Button>
            </Group>
          </Card>
        )}

        <Box>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
            Available ISOs
          </Text>
          {loading ? (
            <Text c="dimmed" size="sm">
              Loading…
            </Text>
          ) : isos.length === 0 ? (
            <Text c="dimmed" size="sm">
              No ISOs available.
            </Text>
          ) : (
            <Stack gap="xs">
              {isos.map((iso) => {
                const mounted = mountedIso?.id === iso.id;
                return (
                  <Group key={iso.id} justify="space-between" align="center" wrap="nowrap">
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Group gap="xs">
                        <Text size="sm" truncate>
                          {iso.display_name}
                        </Text>
                        {mounted && (
                          <Badge size="xs" variant="light">
                            mounted
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed" truncate>
                        {formatBytes(iso.size_bytes)}
                      </Text>
                    </Stack>
                    {iso.present ? (
                      <Button
                        size="xs"
                        variant="default"
                        loading={busy}
                        disabled={mounted}
                        onClick={() => void mount(iso)}
                      >
                        {mounted ? "Mounted" : "Mount"}
                      </Button>
                    ) : (
                      <Tooltip label="This ISO is listed but the file is missing from storage. Contact support.">
                        <Button size="xs" variant="default" disabled>
                          Unavailable
                        </Button>
                      </Tooltip>
                    )}
                  </Group>
                );
              })}
            </Stack>
          )}
        </Box>

        <Alert color="gray" variant="light" p="sm">
          <Text size="xs" c="dimmed">
            After mounting, the CD-ROM is included in your VM's boot order. Restart the VM to boot from this ISO.
          </Text>
        </Alert>
      </Stack>
    </Modal>
  );
}
